import React, { useState } from "react";
import { useForm } from "react-hook-form";
import DotIcon from "../../images/svgs/DotIcon";
import Eye from "../../images/svgs/Eye";
import { api1 } from "../../utils/axiosInstance";
import Modal from "../modal/Modal";
import StarRating from "../starRating/StarRating";
import FlexibleTextarea from "../textArea/FlexibleTextarea";
import { Link } from "react-router-dom";
import Message from "../../images/icons/chat.png";

const statusColors = {
  "UP COMING": "bg-yellow-500 text-white",
  COMPLETED: "bg-accept text-white",
  CANCELLED: "bg-reject text-white",
};

const BookingTable = ({ headers, data, title, handleViewRecipt }) => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [mentorId, setMentorId] = useState(null);
  const [mentorName, setMentorName] = useState(null);
  // Open review modal
  const openReviewModal = (row) => {
    setIsReviewModalOpen(true);
    setMentorId(row.details.mentorId._id);
    setMentorName(row["MENTOR NAME"]);
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const handleReviewModalClose = () => {
    reset();
    setRating(0);
    setIsReviewModalOpen(false);
  };
  // Form submission handler for review
  const onSubmitReview = async (data) => {
    const fullData = {
      ...data,
      rating,
      mentorId,
    };
    try {
      await api1.post("/api/review/create", fullData);
    } catch (error) {
      console.log("Error submitting review: ", error);
    } finally {
      handleReviewModalClose();
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg w-full">
      <h2 className="text-xl font-bold mb-4">{title}</h2>

      {data.length === 0 ? (
        <p className="text-gray-500 text-center py-6">
          No appointments at the moment.
        </p>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="border-t border-b">
                <tr>
                  {headers.map((header, index) => (
                    <th key={index} className="py-3 px-4 text-left">
                      {header}
                    </th>
                  ))}
                  <th className="py-3 px-4 text-left">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-t">
                    {headers.map((header, colIndex) => (
                      <td key={colIndex} className="py-3 px-4">
                        {header === "TAGS" ? (
                          <span
                            className={`px-3 py-1 text-sm font-medium rounded ${
                              statusColors[row[header]]
                            }`}
                          >
                            {row[header]}
                          </span>
                        ) : header === "IMAGE" ? (
                          <img
                            src={row[header]}
                            alt="User"
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          row[header]
                        )}
                      </td>
                    ))}
                    <td>
                      <Link
                        to={`/dashboard/chat/${row.details.mentorId._id}`}
                      >
                        <button className="text-blue-800 hover:underline flex items-center justify-center gap-2 bg-blue-100 p-1 px-3 rounded relative">
                          <img src={Message} className="w-5" />
                          Chat with Mentor
                        </button>
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      {row["TAGS"] === "COMPLETED" ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewRecipt(row)}
                            className="text-custom-blue hover:underline flex items-center gap-2 bg-[#1D1E9D12] py-1 px-2 rounded"
                          >
                            <Eye className="text-custom-blue w-6" /> View
                            Receipt
                          </button>
                          <button
                            onClick={() => openReviewModal(row)}
                            className="text-purple-600 hover:underline flex items-center gap-2 bg-purple-100 py-1 px-2 rounded"
                          >
                            Leave Review
                          </button>
                        </div>
                      ) : row["TAGS"] === "UP COMING" ? (
                        <a
                          href={row?.details.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block"
                        >
                          <button className="text-green-500 hover:underline flex items-center gap-2 bg-green-100 py-1 px-2 rounded">
                            <DotIcon className="text-green-500 w-6" />
                            Join Meeting
                          </button>
                        </a>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="lg:hidden space-y-4">
            {data.map((row, rowIndex) => (
              <div key={rowIndex} className="border p-4 rounded-lg shadow">
                {headers.map((header, colIndex) => (
                  <div key={colIndex} className="mb-2">
                    <p className="font-medium">{header}</p>
                    {header === "IMAGE" ? (
                      <img
                        src={row[header]}
                        alt="User"
                        className="w-10 h-10 rounded-full mt-2"
                      />
                    ) : header === "TAGS" ? (
                      <span
                        className={`inline-block px-3 py-1 text-sm font-medium rounded w-full text-center ${
                          statusColors[row[header]]
                        }`}
                      >
                        {row[header]}
                      </span>
                    ) : (
                      <p className="text-sm text-gray-500">{row[header]}</p>
                    )}
                  </div>
                ))}
                <Link to={`/dashboard/chat/${row.details.mentorId._id}`}>
                  <button className="text-blue-800 hover:underline flex items-center justify-center gap-2 bg-blue-100 p-1 px-3 rounded relative w-full">
                    <img src={Message} className="w-5" />
                    Chat with Mentor
                  </button>
                </Link>
                {row["TAGS"] === "COMPLETED" ? (
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleViewRecipt(row)}
                      className="w-full mt-2 flex justify-center items-center gap-2 text-custom-blue bg-[#1D1E9D12] py-2 rounded"
                    >
                      <Eye className="text-custom-blue w-6" /> View Receipt
                    </button>
                    <button
                      onClick={() => openReviewModal(row)}
                      className="w-full flex justify-center items-center gap-2 text-purple-600 bg-purple-100 py-2 rounded"
                    >
                      Leave Review
                    </button>
                  </div>
                ) : row["TAGS"] === "UP COMING" ? (
                  <a
                    href={row?.details.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <button className="w-full mt-2 flex justify-center items-center gap-2 text-green-500 bg-green-100 py-2 rounded">
                      <DotIcon className="text-green-500 w-6" />
                      Join Meeting
                    </button>
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Review Modal */}
      <Modal
        isOpen={isReviewModalOpen}
        onClose={handleReviewModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="flex flex-col gap-8 p-5">
          <form onSubmit={handleSubmit(onSubmitReview)}>
            <div className="flex flex-col items-center gap-10">
              <h2 className="text-xl font-semibold text-gray-900">
                Leave a Review for {mentorName}
              </h2>
              <p className="text-base font-normal">
                How would you rate this session?
              </p>
              <StarRating
                rating={rating}
                onRatingChange={setRating}
                width="w-8 lg:w-16"
                height="h-8 lg:h-16"
              />
            </div>

            <FlexibleTextarea
              name="review"
              register={register}
              required
              errors={errors}
              placeholder="Write a review..."
            />
            <button
              type="submit"
              className="mt-6 bg-custom-blue text-white font-medium py-2 px-6 rounded-full w-full transition-all duration-300 ease-in-out hover:opacity-80"
            >
              Submit Review
            </button>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default BookingTable;
